import React, { useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import { THEME } from 'utils'
import FormsStore from 'stores/forms'
import { useStore } from 'hooks'
import GlobalStyles from 'components/GlobalStyles'
import GeoLocationContainer from 'components/GeoLocationContainer'
import MinimalFooter from 'components/MinimalFooter'
import { SportsbookCheckbox } from 'components/ui'

const FormStartingSchema = Yup.object().shape({
  email: Yup.string().email('Must be a valid email').required('Email is required')
})

const AgreementLabel = (
  <>
    <p>
      I agree to the Penn National Gaming
      <a href='https://www.pngaming.com/terms-of-use' rel='noreferrer' target='_blank'>
        {' '}
        Terms of Use{' '}
      </a>
      and
      <a href='https://www.pngaming.com/privacy-policy' rel='noreferrer' target='_blank'>
        {' '}
        Privacy Policy{' '}
      </a>
      & Barstool Sports
      <a href='/terms-of-use' target='_blank'>
        {' '}
        Terms of Use{' '}
      </a>
      and
      <a href='/privacy-policy' target='_blank'>
        {' '}
        Privacy Policy;{' '}
      </a>
      I am over 21 and not prohibited to wager for any reason, including being placed on any state self-exclusion or
      disassociated persons list.
    </p>
    <style jsx>{`
      p {
        font-family: 'IBM Plex Sans Condensed';
        font-size: 11px;
        letter-spacing: 0.5px;
        line-height: 16px;
        color: #757575;
      }
      a {
        color: #1555d0;
      }
    `}</style>
  </>
)

const LandingPageLayoutAndForm = () => {
  const router = useRouter()
  const [confirmedAgreement, setConfirmedAgreement] = useState(false)
  const formsStore = useStore(FormsStore)
  const handleSubmit = useCallback(
    ({ email }) => {
      // safeguard moving on and submitting if the checkbox hasn't been selected
      if (!confirmedAgreement) return

      // create a submission in progress
      formsStore.startSubmissionInProgress({ email })

      //move to next page
      router.push('/barstool-sportsbook/info')
      return
    },
    [formsStore, router, confirmedAgreement]
  )
  const handleCheckboxToggle = useCallback(() => {
    setConfirmedAgreement(!confirmedAgreement)
  }, [confirmedAgreement, setConfirmedAgreement])

  return (
    <>
      <div className='page'>
        <div className='body background'>
          <div className='logo-container'>
            <img className='logo' src='/static/images/sportsbook/turn-around.gif' />
          </div>
          <div className='form-container'>
            <div className='form-wrapper'>
              <GeoLocationContainer redirectUrl='/barstool-sportsbook/unavailable'>
                <h1>Stay connected and be the first to know when we launch in your state.</h1>
                <hr />
                <p>
                  Provide your contact information and we will update you on upcoming launches and events for the
                  Barstool Sportsbook.
                </p>
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={FormStartingSchema}
                  validateOnBlur={false}
                  validateOnChange={false}
                  enableReinitialize={true}
                  onSubmit={handleSubmit}
                >
                  {({ values }) => (
                    <Form className='sportsBookForm'>
                      <div className='submissionArea'>
                        <Field
                          type='email'
                          name='email'
                          placeholder='EMAIL ADDRESS'
                          id='emailField'
                          className='sportsBook__formField'
                        />
                        <ErrorMessage name='email' component='div' className='field__error' />
                        <SportsbookCheckbox label={AgreementLabel} id='disclaimer' onChange={handleCheckboxToggle} />
                        <div className='button-container'>
                          <button
                            type='submit'
                            disabled={!confirmedAgreement || !values?.email}
                            className={`sportsBook__submitButton${
                              !confirmedAgreement || !values?.email ? '--disabled' : ''
                            }`}
                          >
                            Continue
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
                <div className='pennAffiliation'>
                  <div className='pennLogoContainer'>
                    <img className='pennLogo' src='/static/images/sportsbook/pennlogo.png' />
                  </div>
                  <p>Barstool Sportsbook operated by Penn National Gaming, Inc. and licensed subsidiaries.</p>
                </div>
                <div className='gamblingDisclaimer'>
                  <p>
                    Gambling Problem? Call <a href='tel:18004262537'>1-800-GAMBLER</a> for help
                  </p>
                </div>
              </GeoLocationContainer>
            </div>
          </div>
        </div>
        <div className='footerWrapper'>
          <MinimalFooter
            iconDimensions={{ height: '2rem', width: '2rem' }}
            linkToggles={{ help: false, termsOfUse: true, privacyPolicy: true, subscriptionTerms: false }}
            backgroundColor='#0b1224'
            foregroundColor='#fff'
            fontSize='12px'
          />
        </div>
      </div>
      <GlobalStyles />
      <style jsx>{`
        .page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background-color: #0b1224;
        }
        .background {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          text-align: center;
          padding: 0;
        }
        .body {
          min-height: 80vh;
        }
        .footerWrapper {
          min-height: 20vh;
          width: 100%;
        }
        .logo-container {
          width: 100%;
          height: auto;
        }
        .logo {
          width: 100%;
          max-width: 468px;
          height: auto;
        }
        .logo img {
          border: 2px solid #0b1224;
          box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.8);
        }
        .form-wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: auto;
          min-height: 586px;
          padding: 41px 26px;
          background-color: #fff;
          border-radius: 2px;
          text-align: left;
        }
        .form-container h1 {
          color: #c62232;
          font-family: 'Cooper Hewitt';
          font-weight: 400;
          text-transform: bolder;
          font-size: 34px;
          line-height: 38px;
          letter-spacing: 0;
          margin: 0 0 32px 0;
        }
        .form-container hr {
          margin: 0 0 1rem 0;
        }
        .form-container p {
          color: #010101;
          font-family: 'IBM Plex Sans Condensed';
          font-size: 1rem;
          letter-spacing: 0;
          line-height: 24px;
          padding: 1rem 0;
        }
        .form-container {
          padding: 1rem;
          max-width: 500px;
          width: 100%;
          margin: 0 auto;
        }
        .button-container {
          width: 100%;
          justify-content: center;
          text-align: center;
          padding: 0 0 1rem;
        }
        .button-container button {
          max-width: 350px;
          margin: auto;
        }
        .pennAffiliation {
          display: flex;
          padding: 1rem 0 0.5rem;
          flex-wrap: 'no-wrap';
        }
        .pennAffiliation p {
          color: #020202;
          font-family: 'IBM Plex Sans Condensed';
          font-size: 11px;
          letter-spacing: 0.5px;
          line-height: 16px;
          padding: 0 0 0 1rem;
        }
        .pennLogoContainer {
          max-height: 60px;
        }
        .pennLogo {
          width: auto;
          height: 32px;
        }
        .gamblingDisclaimer p {
          color: #020202;
          font-family: 'IBM Plex Sans Condensed';
          font-size: 11px;
          letter-spacing: 0.5px;
          line-height: 16px;
          margin-bottom: 0px;
          padding: 0;
        }
        .gamblingDisclaimer a {
          color: #1555d0;
        }
        @media (min-width: ${THEME.BREAKPOINTS.LG}px) {
          .background {
            background-color: #0b1224;
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
            padding: 80px 0 0 0;
            justify-content: space-between;
          }
          .body {
            min-height: 60vw;
          }
          .logo-container {
            flex-basis: 50%;
            padding: 0;
            margin: 0;
          }
          .logo {
            width: 100%;
            max-width: 800px;
            padding: 0 1rem;
            height: auto;
          }
          .form-container {
            flex-basis: 50%;
            height: 60%;
            max-width: 800px;
            padding: auto 1rem;
          }
          .form-wrapper {
            padding: 4rem;
          }
          .footerWrapper {
            flex-basis: 100%;
            background-color: #0b1224;
          }
        }
        @media (min-width: ${THEME.BREAKPOINTS.XL}px) {
          .body {
            min-height: 0 !important;
          }
        }
      `}</style>
    </>
  )
}

export default LandingPageLayoutAndForm
